import { z } from 'zod';

/** Typed error codes (SDD §13.1 Errors). Messages are user-safe; detail lives in logs by requestId.
 *  The shape is a discriminated union on `code` so clients branch without string matching. */
export const ERROR_CODES = [
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'LOCKED',
  'NOT_ENTITLED',
  'RATE_LIMITED',
  'VALIDATION',
  'CONFLICT',
  'UPSTREAM_DEGRADED',
  'UNCONFIRMED',
  'INTERNAL',
] as const;
export const ErrorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

export const ValidationIssueSchema = z.object({ path: z.string(), message: z.string() }).strict();
export type ValidationIssue = z.infer<typeof ValidationIssueSchema>;

const base = { message: z.string() };
export const AppErrorSchema = z.discriminatedUnion('code', [
  z.object({ code: z.literal('UNAUTHENTICATED'), ...base }).strict(),
  z.object({ code: z.literal('FORBIDDEN'), ...base }).strict(),
  z
    .object({
      code: z.literal('LOCKED'),
      ...base,
      /** Why the action is locked — a rule key, a lockout id or a gate name (e.g. NO_ARMED_SESSION). */
      reason: z.string(),
      /** ISO time the lock lifts; null when it needs an explicit action (e.g. arm a session). */
      unlocksAt: z.string().nullable(),
      /** What produced the lock: rule engine, session machine, kill switch, … */
      source: z.string(),
    })
    .strict(),
  z.object({ code: z.literal('NOT_ENTITLED'), ...base, requiredTier: z.string() }).strict(),
  z.object({ code: z.literal('RATE_LIMITED'), ...base, retryAfter: z.number().int().nonnegative() }).strict(),
  z.object({ code: z.literal('VALIDATION'), ...base, issues: z.array(ValidationIssueSchema) }).strict(),
  z.object({ code: z.literal('CONFLICT'), ...base }).strict(),
  z.object({ code: z.literal('UPSTREAM_DEGRADED'), ...base, source: z.string() }).strict(),
  z.object({ code: z.literal('UNCONFIRMED'), ...base, intentId: z.string() }).strict(),
  z.object({ code: z.literal('INTERNAL'), ...base, requestId: z.string() }).strict(),
]);
export type AppErrorShape = z.infer<typeof AppErrorSchema>;
export type AppErrorOf<C extends ErrorCode> = Extract<AppErrorShape, { code: C }>;

/** The one error type a procedure may reject with. `shape` is the wire form (zod-validated); `code` is the discriminant. */
export class AppError extends Error {
  readonly code: ErrorCode;
  readonly shape: AppErrorShape;
  constructor(shape: AppErrorShape) {
    const parsed = AppErrorSchema.parse(shape);
    super(parsed.message);
    this.name = 'AppError';
    this.code = parsed.code;
    this.shape = parsed;
  }
  /** Narrow to one code: `if (e.is('LOCKED')) e.shape.unlocksAt`. */
  is<C extends ErrorCode>(code: C): this is AppError & { shape: AppErrorOf<C> } {
    return this.code === code;
  }
  toJSON(): AppErrorShape {
    return this.shape;
  }
  /** Rebuild from the wire form (e.g. a tRPC error payload in Phase 2). */
  static from(value: unknown): AppError | null {
    const parsed = AppErrorSchema.safeParse(value);
    return parsed.success ? new AppError(parsed.data) : null;
  }
}
export function isAppError(e: unknown): e is AppError {
  return e instanceof AppError;
}
